import { createFileRoute } from "@tanstack/react-router";
import { canonicalUrl, absUrl, canonicalLink, geoMeta, keywordsWithLocal } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SunbrellaLifestyleShop } from "@/components/SunbrellaLifestyleShop";

import heroImg from "@/assets/original/hero-shade.jpg";

const TITLE = "Shop Sunbrella® Lifestyle — Authorized Retailer | Texas Made Shade";
const DESC =
  "Curated Sunbrella® Lifestyle goods from an Authorized Retailer in Seguin, TX — outdoor curtains, throws, care kits. Serving San Antonio & Austin.";

export const Route = createFileRoute("/shop")({
  head: () => ({
    links: [canonicalLink("/shop")],
    meta: [
      ...geoMeta(),{ property: "og:url", content: canonicalUrl("/shop") },
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content: keywordsWithLocal(
          "Sunbrella outdoor curtains, Sunbrella throws, Sunbrella cleaning products, Sunbrella Clean Restore Renew, Sunbrella Total Care Kit, Sunbrella 1961 tote, authorized Sunbrella retailer Texas, buy Sunbrella online Texas, outdoor drapery panels, indoor outdoor throw blanket, patio curtains San Antonio, patio curtains Austin, custom outdoor curtains Texas",
        ),
      },
      { name: "geo.placename", content: "San Antonio, Austin, Seguin, Texas" },
      { name: "location", content: "San Antonio, Austin, Texas" },
      { property: "og:title", content: "Shop Sunbrella® Lifestyle — Authorized Retailer" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:image", content: absUrl(heroImg) },
      { property: "og:image:secure_url", content: absUrl(heroImg) },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: absUrl(heroImg) },
    ],
    scripts: pageHeadScripts({
      path: "/shop",
      name: "Shop Sunbrella® Lifestyle — Texas Made Shade",
      description: DESC,
    }),
  }),
  component: ShopPage,
});

function ShopPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        {/* HERO BANNER */}
        <section className="relative bg-navy-deep py-20 text-white lg:py-28">
          <div className="absolute inset-0">
            <img
              src={heroImg}
              alt="Outdoor patio with Sunbrella fabric shade and curtains by Texas Made Shade in Seguin, TX"
              className="h-full w-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/85 to-navy-deep/60" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
            <div className="font-display text-sm tracking-[0.3em] text-red">
              ★ AUTHORIZED SUNBRELLA® LIFESTYLE RETAILER
            </div>
            <h1 className="mt-3 font-display text-5xl font-bold leading-[0.95] md:text-7xl">
              Shop Sunbrella®<br />
              <span className="text-white/90">Outdoor Living</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/80">
              A small, curated assortment — outdoor curtain panels, indoor/outdoor throws,
              the official cleaning & care line and one very good tote. Everything here is
              genuine Sunbrella®, offered by the crew that fabricates and installs shade
              across South and Central Texas.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#curtains"
                className="inline-flex items-center gap-2 bg-red px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-red"
              >
                Shop Outdoor Curtains
              </a>
              <a
                href="#order-request"
                className="inline-flex items-center gap-2 border-2 border-white/40 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:border-white"
              >
                Start an Order Request
              </a>
            </div>
          </div>
        </section>

        <SunbrellaLifestyleShop />
      </main>
      <SiteFooter />
    </div>
  );
}
